/**
 * The RESOLVABLE conflict card — W2 (identity mismatch on an exact LinkedIn
 * match) and W3 (a name match nothing could confirm). Both ask the same
 * question of the recruiter, so both wear the same card.
 *
 * Rebuilt 2026-09-08 on `ConflictCardShell`, which owns the grammar (left =
 * your row, right = `In Thrive (N)` as compact rows, one verdict line, the rest
 * behind an ⓘ). What survives here is the SEMANTICS: which actions this card
 * offers, and why.
 *
 * ACTIONS — one mutually-exclusive list, and SINCE fu5 SUB-TASK 02 IT STARTS
 * WITH NOTHING SELECTED. `action` is nullable; null renders as no option
 * filled, and the modal's Continue stays disabled until every row has one. The
 * neutral `Do nothing` arm that used to open every set is deleted, because a
 * pre-selected default made "nobody looked at this row" and "somebody looked
 * and left it alone" the same value. The request-time arms flip a batched
 * decision carried to the Export payload; the persisted arms call their own
 * APIs. All of it is reported upward: this component owns no semantics.
 *
 * The options are FULL SENTENCES on full-width highlight rows
 * (`HighlightOptionGroup`), not segments — the three arguments for that are on
 * `CONFLICT_ACTION_SENTENCES`, and each has to be defeated separately.
 *
 * WHICH ARMS RENDER is decided by two facts about the contact, not by the card
 * kind alone — see `conflictActionValues` below for the full derivation, which
 * the modal's Continue gate reads too. In short:
 *
 *   - `Update anyway` needs a confirmed contact to update. A W3 has none: the
 *     worker withholds its unconfirmed candidate id (`bulk_set_match_results`
 *     writes NULL), so `resolve_export_plan` reaches the CREATE arm and no
 *     update ever happened — while the button said "Update anyway". That label
 *     was wrong on every W3 card that ever rendered.
 *   - `Add as new contact` creates the row and leaves Thrive's record alone.
 *   - `Not the same person` needs BOTH a candidate id and a LinkedIn URL: it
 *     writes a firm-wide registry pair keyed on the canonical URL. It used to
 *     render DISABLED with a tooltip when the URL was missing — honest, and
 *     useless: the recruiter reading "needs a LinkedIn URL" still had no way to
 *     say "this is someone else, create her". `Add as new contact` takes that
 *     slot, being the same answer minus the firm-wide memory it cannot have.
 *
 * The Thrive avatar is `null` whenever Thrive genuinely has none on file
 * (common) OR the best-effort lookup failed — both render the same placeholder
 * silhouette; this card doesn't distinguish them.
 */

import type { ReactNode } from "react";
import type { ConflictContact } from "@nu-assist/contracts";
import { type HighlightOption, HighlightOptionGroup } from "@nu-assist/ui";
import { InfoHint } from "../../routes/research/import-detail/InfoHint";
import { ConflictCardShell, type ThriveSideEntry } from "./ConflictCardShell";
import {
  ADD_AS_NEW_DETAIL,
  CONFLICT_ACTION_CAPTION,
  CONFLICT_ACTION_SENTENCES,
  type ConflictAction,
  LEAVE_OUT_HELPER,
  LEFT_OUT_PILL,
  UNDO_LEAVE_OUT_LABEL,
  W2_DETAIL,
  W2_VERDICT,
  W3_DETAIL,
  W3_VERDICT,
  formatIgnoredDate,
} from "./conflictActionsCopy";

/** Which conflict class this card renders. Both use the same layout and the
 *  same action list; the kind decides the verdict copy, the proceed arm and the
 *  testid prefix. */
export type ConflictCardKind = "w2" | "w3";

export interface W2ConflictCardProps {
  contact: ConflictContact;
  /** The selected action, or `null` for UNDECIDED — the state a conflict row
   *  starts in and the one the recruiter has to leave.
   *
   *  NULLABLE SINCE fu5 sub-task 02. It used to be a non-null `ConflictAction`
   *  defaulting to `doNothing`, which made "nobody looked at this row" and
   *  "somebody looked and chose to leave it alone" the same value. The parent
   *  owns the state; this card only renders it, and renders `null` as no option
   *  filled. */
  action: ConflictAction | null;
  /** Inline error from a failed PERSISTED action (null = none). */
  actionError?: string | null;
  /** Reports a click on a non-selected action. The parent maps it to semantics:
   *  `updateAnyway` / `addAsNew` flip the BATCHED per-contact decision (written
   *  when the flow continues — never an immediate per-row write);
   *  `removeFromList` / `notSamePerson` call their APIs. */
  onActionChange: (action: ConflictAction) => void;
  /** Defaults to `"w2"` so existing W2 callers are unchanged. */
  kind?: ConflictCardKind;
  /** Presigned S3 GET URL for the SOURCE contact's stored avatar, or undefined
   *  while the presign is in flight. The licdn URL is never rendered. */
  resolvedAvatarUrl?: string | null;
  /** True when the parent can persist actions (api + importId present). */
  persistedActionsEnabled?: boolean;
  /** True when this contact carries a persisted dismissal. */
  leftOut?: boolean;
  /** When the dismissal was recorded — the "skipped <date>" line. */
  leftOutAt?: string | null;
  /** Undo a leave-out. */
  onUndoLeaveOut?: () => void;
  /** What happened to this row's write, rendered in the shell's slot.
   *
   *  A NODE the parent builds, for the same reason `DifferentPersonCard` takes
   *  one: this card must not learn what an add outcome is, nor which flag
   *  reveals a celebration. Absent until 2026-09-11, which is why a card the
   *  recruiter had just resolved with "Same person" sat there saying nothing —
   *  the write happened, and the screen never mentioned it. */
  addStatus?: ReactNode;
}

function fullName(first?: string | null, last?: string | null): string {
  return [first, last].filter(Boolean).join(" ").trim();
}

/**
 * WHICH ARMS THIS CARD OFFERS — the one derivation, exported so the modal's
 * Continue gate reads the SAME answer the card renders.
 *
 * Exported rather than duplicated because the gate has to know whether a row
 * can be decided AT ALL: a card that renders no options can never be answered,
 * and counting such a row as "undecided" would disable Continue forever. Two
 * independent copies of these three predicates would agree today and drift on
 * the first change to any of them — the twin defect class this epic keeps
 * removing.
 *
 * The predicates themselves are UNCHANGED by fu5 sub-task 02 (each is justified
 * in place below); 02 only deleted the `doNothing` arm every set used to open
 * with. Every surviving set is non-empty on a live surface, and the common W2
 * case is exactly three.
 */
export function conflictActionValues(
  contact: ConflictContact,
  _kind: ConflictCardKind,
  persistedActionsEnabled: boolean
): ConflictAction[] {
  // WHETHER THERE IS A THRIVE CONTACT TO UPDATE — asked of the payload, not of
  // the card kind. The `kind === "w2"` version of this read as "a W3 never
  // carries a candidate id", which the worker's own output disproves: Lee Park
  // arrives as `match=fuzzy_name` WITH `thrive_id` 7 (operator, 2026-09-12).
  // He was left holding Skip and "Not the same person" — neither of which is
  // the answer when the match is right and only the name spelling differs.
  //
  // Relaxing this alone would have been wrong, and the comment below said so:
  // the row's `contact_id_thrive` column is NULL on a W3, so the export would
  // CREATE regardless of the button's label. That is why the decision now ships
  // `confirm_thrive_id` — the match is persisted BEFORE the export runs, so the
  // guarantee this predicate used to approximate is enforced where it belongs.
  const canUpdateExisting = Boolean(contact.contact_id_thrive);
  // The registry needs both halves of a pair. Without a URL there is no
  // identity to remember the decision under — that is a fact about the
  // registry, not about this contact's photo.
  const canRegisterDecision = Boolean(contact.contact_id_thrive) && Boolean(contact.linkedin_url);
  // The escape hatch is offered exactly where the durable answer is not. Note
  // it does NOT read `persistedActionsEnabled`: that flag is about whether this
  // surface can call the APIs at all (isolated tests, mostly), not about
  // whether this contact has a registry identity — gating on it would hide the
  // request-time action on a card that has no server call to make.
  const showAddAsNew = !canRegisterDecision;

  const values: ConflictAction[] = [];
  if (persistedActionsEnabled) {
    values.push("removeFromList");
    if (canRegisterDecision) values.push("notSamePerson");
  }
  // WHICH proceed arms this card offers, and it is not "one of two":
  //
  //  · `Update anyway` needs a Thrive contact to update, and offers itself
  //    wherever the payload names one — on a W3 as much as on a W2.
  //  · `Add as new contact` is offered whenever the DURABLE way to say "this is
  //    somebody else" is not: no candidate id, or no LinkedIn URL for the
  //    registry to key on. That is most W3s, and it is also a W2 whose row has
  //    no usable URL — the identity question is open there too, and without
  //    this the only reachable answers were "update the wrong person" and
  //    "drop her".
  //
  // The two DO appear together, and that pair is the POINT rather than a bug:
  // on a fuzzy name match with a named holder, "these are the same person,
  // spelt differently" and "these are two people" are the two live readings,
  // and the card has to offer both. They are not near-duplicates — one updates
  // Thrive #N, the other leaves it alone and creates a contact. Before
  // `confirm_thrive_id` the first of those was a lie on a W3 (the export
  // created either way), which is what the old `kind === "w2"` guard was
  // protecting against; the persistence now makes the label true.
  if (canUpdateExisting) values.push("updateAnyway");
  if (showAddAsNew) values.push("addAsNew");
  return values;
}

/** Per-option `data-testid`, kept in ONE place so the card, the modal's tests
 *  and the E2E cannot drift on the naming. */
const ACTION_TEST_IDS: Record<ConflictAction, string> = {
  removeFromList: "remove-from-list",
  notSamePerson: "not-same-person",
  updateAnyway: "update-anyway",
  addAsNew: "add-as-new",
};

export function W2ConflictCard({
  contact,
  action,
  actionError = null,
  onActionChange,
  kind = "w2",
  resolvedAvatarUrl,
  persistedActionsEnabled = false,
  leftOut = false,
  leftOutAt = null,
  onUndoLeaveOut,
  addStatus,
}: W2ConflictCardProps) {
  const sourceName = fullName(contact.first_name, contact.last_name) || "This row";
  const id = contact.import_contact_id;

  // The one derivation, shared with the modal's Continue gate. See
  // `conflictActionValues` above for why it is not inlined here any more.
  const actionValues = conflictActionValues(contact, kind, persistedActionsEnabled);
  const showAddAsNew = actionValues.includes("addAsNew");

  const thriveEntries: ThriveSideEntry[] = contact.contact_id_thrive
    ? [
        {
          thriveId: contact.contact_id_thrive,
          name: fullName(contact.thrive_first_name, contact.thrive_last_name),
          company: contact.thrive_company,
          title: contact.thrive_title,
          avatarUrl: contact.thrive_avatar_url,
        },
      ]
    : [];

  // FULL SENTENCES on full-width rows, not short labels on segments. The
  // sentences live in `CONFLICT_ACTION_SENTENCES` with the three arguments for
  // the shape; the testids keep their old `<kind>-<action>-<id>` grammar so the
  // E2E and the modal tests did not have to be re-learned along with the copy.
  const actionOptions: HighlightOption<ConflictAction>[] = actionValues.map((value) => ({
    value,
    label: CONFLICT_ACTION_SENTENCES[value],
    testId: `${kind}-${ACTION_TEST_IDS[value]}-${id}`,
  }));

  const banner = leftOut ? (
    <div className="flex flex-col gap-0.5">
      <div className="flex flex-wrap items-center gap-2">
        <span
          data-testid={`${kind}-left-out-pill`}
          className="rounded-full border border-border bg-bg px-2 py-0.5 text-caption text-text-secondary"
        >
          {LEFT_OUT_PILL}
        </span>
        <span className="text-caption text-text-secondary">{LEAVE_OUT_HELPER}</span>
      </div>
      {/* The date gets its OWN line, with the time. Beside the pill it was
          competing with the helper sentence for the same row and lost. */}
      {leftOutAt && (
        <span
          data-testid={`${kind}-ignored-date-${id}`}
          className="text-caption text-text-secondary"
        >
          {formatIgnoredDate(new Date(leftOutAt), new Date())}
        </span>
      )}
    </div>
  ) : null;

  const footer = (
    <div className="flex flex-col items-center gap-1.5">
      {leftOut ? (
        <button
          type="button"
          data-testid={`${kind}-undo-leave-out-${id}`}
          className="rounded-md border border-border bg-bg px-2 py-1 text-label text-text hover:bg-surface"
          onClick={onUndoLeaveOut}
        >
          {UNDO_LEAVE_OUT_LABEL}
        </button>
      ) : (
        <>
          <div className="flex items-start gap-1">
            <p className="text-caption text-text-secondary">{CONFLICT_ACTION_CAPTION}</p>
            {/* The ⓘ rides the CAPTION, not an option row: the durability
                caveat is about the choice, not about one arm's mechanics. An
                option's own `trailing` slot exists for nested interactives, and
                putting a popover trigger there would compete with the row's
                click target for no gain. */}
            {showAddAsNew && (
              <InfoHint
                content={ADD_AS_NEW_DETAIL}
                label="About adding as a new contact"
                escapesOverflow
              />
            )}
          </div>
          {/* `value={action}` where `action` may be null — UNDECIDED, and the
              primitive renders it legally (nothing filled, group
              `aria-required`). This card no longer has a default to fall back
              on, which is the entire behavioural change of fu5 sub-task 02. */}
          <HighlightOptionGroup<ConflictAction>
            aria-label="Conflict action"
            data-testid={`${kind}-conflict-actions-${id}`}
            options={actionOptions}
            value={action}
            onChange={onActionChange}
          />
        </>
      )}
      {actionError ? (
        <p
          data-testid={`${kind}-action-error-${id}`}
          className="text-label text-error-ink"
          role="alert"
        >
          {actionError}
        </p>
      ) : null}
    </div>
  );

  return (
    <ConflictCardShell
      celebration={addStatus}
      anchorId={id}
      testId={`${kind}-conflict-card`}
      source={{
        name: sourceName,
        company: contact.company,
        title: contact.title,
        linkedinUrl: contact.linkedin_url,
        // The ONLY source avatar is the durable S3 copy: a contact WITH a
        // stored key renders its presigned URL (undefined while resolving →
        // initials); no stored key → initials. The licdn `profile_image_url` is
        // never rendered (it 403s / flashes broken).
        avatarUrl: contact.profile_image_s3_key ? resolvedAvatarUrl : undefined,
      }}
      thrive={thriveEntries}
      verdict={kind === "w3" ? W3_VERDICT : W2_VERDICT}
      verdictDetail={kind === "w3" ? W3_DETAIL : W2_DETAIL}
      verdictTone="warning"
      banner={banner}
      footer={footer}
      dimmed={leftOut}
    />
  );
}
